import dayjs from "dayjs";
import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { stringifyParams } from "../../../../utils/stringify.js";
import { totalRevenueSalesApi } from "../api";
import { API_DATE_FORMAT } from "../../../../app_constants.js";

export const SET_LOADING = "saga/totalRevenueSales/SET_LOADING";
export const SET_UPDATES_LOADING = "saga/totalRevenueSales/SET_UPDATES_LOADING";
export const SET_FILTERS_DIRTY = "saga/totalRevenueSales/SET_FILTERS_DIRTY";
export const FETCH_CURRENT_USER = "saga/totalRevenueSales/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/totalRevenueSales/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_CHART_SUCCESS = "saga/totalRevenueSales/FETCH_CHART_SUCCESS";
export const FETCH_GRAPH_FAILURE = "saga/totalRevenueSales/FETCH_GRAPH_FAILURE";
export const FETCH_FILTERS_SUCCESS = "saga/totalRevenueSales/FETCH_FILTERS_SUCCESS";
export const FETCH_FILTERS_FAILURE = "saga/totalRevenueSales/FETCH_FILTERS_FAILURE";

export const UPDATE_CHART = "saga/totalRevenueSales/UPDATE_CHART";

const UPDATE_TABLE_VIEW = "saga/totalRevenueSales/UPDATE_TABLE_VIEW";
export const UPDATE_TABLE_VIEW_SELECTED = "saga/totalRevenueSales/UPDATE_TABLE_VIEW_SELECTED";

export const SET_DATES = "saga/totalRevenueSales/SET_DATES";
export const UPDATE_DATES = "saga/totalRevenueSales/UPDATE_DATES";
export const UPDATE_PERIOD_TYPE = "sagas/UPDATE_PERIOD_TYPE";

const UPDATE_SHOW_NET_REVENUE = "sagas/UPDATE_SHOW_NET_REVENUE";
export const UPDATE_SHOW_NET_REVENUE_SELECTED = "sagas/UPDATE_SHOW_NET_REVENUE_SELECTED";

const UPDATE_SHOW_EXPENSES = "sagas/UPDATE_SHOW_EXPENSES";
export const UPDATE_SHOW_EXPENSES_SELECTED = "sagas/UPDATE_SHOW_EXPENSES_SELECTED";

const RESET_FILTER = "sagas/RESET_FILTER";
export const RESET_FILTER_SELECTED = "sagas/RESET_FILTER_SELECTED";

export const UPDATE_CUSTOM_RANGE = "sagas/UPDATE_CUSTOM_RANGE";

const UPDATE_REVENUE_VIEW = "saga/totalRevenueSales/UPDATE_REVENUE_VIEW";
export const UPDATE_REVENUE_VIEW_SELECTED = "saga/totalRevenueSales/UPDATE_REVENUE_VIEW_SELECTED";

const TOGGLE_PREDICTION = "saga/totalRevenueSales/TOGGLE_PREDICTION";
export const TOGGLE_PREDICTION_SELECTED = "saga/totalRevenueSales/TOGGLE_PREDICTION_SELECTED";
export const FETCH_PREDICTION_SUCCESS = "saga/totalRevenueSales/FETCH_PREDICTION_SUCCESS";
export const SET_PREDICTION_LOADING = "saga/totalRevenueSales/SET_PREDICTION_LOADING";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateData: createAction(UPDATE_CHART),
  updateDates: createAction(UPDATE_DATES),
  updatePeriodType: createAction(UPDATE_PERIOD_TYPE),
  updateShowNetRevenue: createAction(UPDATE_SHOW_NET_REVENUE),
  updateShowExpenses: createAction(UPDATE_SHOW_EXPENSES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  resetFilters: createAction(RESET_FILTER),
  updateTableView: createAction(UPDATE_TABLE_VIEW),
  updateRevenueView: createAction(UPDATE_REVENUE_VIEW),
  togglePrediction: createAction(TOGGLE_PREDICTION)
};

export default function* watchTotalRevenueSales() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_CHART, updateData);
  yield takeLatest(UPDATE_TABLE_VIEW, updateTableView);
  yield takeLatest(UPDATE_CUSTOM_RANGE, updateCustomRange);
  yield takeLatest(RESET_FILTER, resetFilters);
  yield takeLatest(UPDATE_SHOW_NET_REVENUE, updateShowNetRevenue);
  yield takeLatest(UPDATE_SHOW_EXPENSES, updateShowExpenses);
  yield takeLatest(UPDATE_REVENUE_VIEW, updateRevenueView);
  yield takeLatest(TOGGLE_PREDICTION, togglePrediction);
}

function* updateRevenueView({ payload }) {
  yield put({ type: UPDATE_REVENUE_VIEW_SELECTED, payload });
  yield put({ type: TOGGLE_PREDICTION_SELECTED, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  const endDate = yield select((state) => state.totalRevenueSales.currentUser.calendarEndDate);
  const startDate = dayjs(endDate).startOf("month").subtract(11, "months").format(API_DATE_FORMAT);

  yield setDates(startDate, endDate);
  yield put({ type: RESET_FILTER_SELECTED });

  try {
    yield fetchFilters();
    yield fetchChart();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  const endDate = yield select((state) => state.totalRevenueSales.currentUser.calendarEndDate);
  const startDate = dayjs(endDate).startOf("month").subtract(11, "months").format(API_DATE_FORMAT);

  yield setDates(startDate, endDate);

  yield put({ type: RESET_FILTER_SELECTED });
  try {
    yield fetchChart();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateTableView({ payload }) {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });
  yield put({ type: UPDATE_TABLE_VIEW_SELECTED, payload });
  try {
    yield fetchChart();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateShowNetRevenue({ payload }) {
  yield put({ type: UPDATE_SHOW_NET_REVENUE_SELECTED, payload });

  const showPrediction = yield select((state) => state.totalRevenueSales.showPrediction);

  if (showPrediction) {
    yield fetchPrediction();
  } else {
    yield updateData();
  }
}

function* updateShowExpenses({ payload }) {
  yield put({ type: UPDATE_SHOW_EXPENSES_SELECTED, payload });
  const showPrediction = yield select((state) => state.totalRevenueSales.showPrediction);
  if (showPrediction) {
    yield fetchPrediction();
  } else {
    yield updateData();
  }
}

export function* updateCustomRange() {
  yield put({ type: SET_FILTERS_DIRTY, payload: true });
}

function* setDates(startDate, endDate) {
  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate
    }
  });
}

function* getCommonApiParams() {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);
  let { startDate, endDate } = yield select(
    (state) => state.totalRevenueSales.selectedRequestDates
  );

  return {
    organization_id,
    short_url,
    start_date: startDate,
    end_date: endDate
  };
}

export function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: { currentUser: data.payload }
  });

  const endDate = yield select((state) => state.totalRevenueSales.currentUser.calendarEndDate);
  const startDate = dayjs(endDate).startOf("month").subtract(11, "months").format(API_DATE_FORMAT);

  yield setDates(startDate, endDate);

  try {
    yield fetchFilters();
    yield fetchChart();
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

export function* fetchChart() {
  const commonParams = yield getCommonApiParams();
  const selectedFilters = yield select((state) => state.totalRevenueSales.selectedFilters);
  const selectedView = yield select((state) => state.totalRevenueSales.selectedRevenueView);

  const { period_type, ...rest } = selectedFilters;
  const isRevenueCategoryView = selectedView.id === "revenue_category";

  let params;
  if (isRevenueCategoryView) {
    params = {
      ...commonParams,
      ...stringifyParams(rest),
      period_type: period_type.toLowerCase(),
      view: selectedView.id
    };
  } else {
    const { include_returns, table_view } = rest;
    params = {
      ...commonParams,
      ...stringifyParams({ include_returns, table_view }),
      period_type: period_type.toLowerCase(),
      view: selectedView.id
    };
  }

  yield getTotalRevenueSalesChart(params);
}

export function* fetchFilters() {
  const params = yield getCommonApiParams();
  const selectedView = yield select((state) => state.totalRevenueSales.selectedRevenueView);

  yield getTotalRevenueSalesFilters({ ...params, view: selectedView.id });
}

export function* updateData() {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield fetchChart();
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* getTotalRevenueSalesChart(params) {
  try {
    const response = yield call([totalRevenueSalesApi, totalRevenueSalesApi.getChart], params);
    yield put({ type: FETCH_CHART_SUCCESS, payload: response.data.data });
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_GRAPH_FAILURE, error });
  }
}

function* fetchPrediction() {
  yield put({ type: SET_PREDICTION_LOADING, payload: true });
  const commonParams = yield getCommonApiParams();
  const { include_returns, show_expenses } = yield select(
    (state) => state.totalRevenueSales.selectedFilters
  );
  const { id: view } = yield select((state) => state.totalRevenueSales.selectedRevenueView);
  try {
    const params = { ...commonParams, include_returns, show_expenses, prediction: true, view };
    const response = yield call([totalRevenueSalesApi, totalRevenueSalesApi.getChart], params);
    yield put({ type: FETCH_PREDICTION_SUCCESS, payload: response.data.data });
  } catch (error) {
    console.error(error);
  }
  yield put({ type: SET_PREDICTION_LOADING, payload: false });
}

function* togglePrediction({ payload: enabled }) {
  yield put({ type: TOGGLE_PREDICTION_SELECTED, payload: enabled });
  if (!enabled) {
    yield updateData();
    return;
  }
  yield fetchPrediction();
}

function* getTotalRevenueSalesFilters(params) {
  try {
    const response = yield call([totalRevenueSalesApi, totalRevenueSalesApi.getFilters], params);
    yield put({ type: FETCH_FILTERS_SUCCESS, payload: response.data.filters });

    const { start_date, end_date } = response.data.filters || {};

    yield setDates(start_date, end_date);
  } catch (error) {
    console.error(error);
    yield put({ type: FETCH_FILTERS_FAILURE, error });
  }
}
